import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

interface FacetDraft {
  id?: string;
  title?: string;
  source?: "languages";
  tags?: string[];
  [key: string]: unknown;
}

const SOURCE_MODE = "source";
const TAGS_MODE = "tags";

function asRows(value: unknown): FacetDraft[] {
  return Array.isArray(value) ? (value as FacetDraft[]) : [];
}

/** `kind: "facets"` — `Catalog.facets` rows (`CatalogFacet`: `id`/`title` + EXACTLY
 *  one of `source:"languages"` or `tags[]`). */
export function FacetsEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const rows = asRows(value);
  const commit = (next: FacetDraft[]) => onChange(next.length > 0 ? next : undefined);
  const update = (i: number, patch: Partial<FacetDraft>) =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));
  const add = () => commit([...rows, { id: "", title: "", source: "languages" }]);

  const modeOf = (r: FacetDraft) => (r.source ? SOURCE_MODE : TAGS_MODE);
  const setMode = (i: number, mode: string) =>
    update(i, mode === SOURCE_MODE ? { source: "languages", tags: undefined } : { source: undefined, tags: [] });

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={1.5}>
        {rows.map((row, i) => (
          <Box key={i} sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                label="Facet id"
                value={row.id ?? ""}
                onChange={(e) => update(i, { id: e.target.value })}
                sx={{ minWidth: 140 }}
              />
              <TextField
                size="small"
                label="Title"
                value={row.title ?? ""}
                onChange={(e) => update(i, { title: e.target.value })}
                sx={{ minWidth: 160 }}
              />
              <TextField
                select
                size="small"
                label="Source"
                value={modeOf(row)}
                onChange={(e) => setMode(i, e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value={SOURCE_MODE}>languages</MenuItem>
                <MenuItem value={TAGS_MODE}>tags</MenuItem>
              </TextField>
              <Button size="small" color="error" onClick={() => remove(i)}>
                Remove
              </Button>
            </Box>
            {modeOf(row) === TAGS_MODE ? (
              <TextField
                fullWidth
                size="small"
                label="Tags (comma-separated)"
                value={(row.tags ?? []).join(", ")}
                onChange={(e) =>
                  update(i, {
                    tags: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter((s) => s.length > 0),
                  })
                }
              />
            ) : null}
          </Box>
        ))}
        <Button size="small" variant="outlined" onClick={add} sx={{ alignSelf: "flex-start" }}>
          + Add facet
        </Button>
      </Stack>
    </Box>
  );
}
